import { queryApi } from "../api"
import { handleQueryApiError, onQueryStartedErrorHandler } from "../utility/error"
import {
    CHANGE_APPLICATION_TEMPLATE_STATUS_FAILED,
    CREATE_APPLICATION_TEMPLATE_FAILED,
    CREATE_APPLICATION_TEMPLATE_VERSION_FAILED,
    DELETE_APPLICATION_TEMPLATE_FAILED,
    DELETE_APPLICATION_TEMPLATE_VERSION_FAILED,
    GET_APPLICATION_TEMPLATE_ALL_VERSIONS_FAILED,
    GET_APPLICATION_TEMPLATE_FAILED,
    GET_APPLICATION_TEMPLATE_VERSION_FAILED,
    GET_APPLICATION_TEMPLATE_VERSIONS_FAILED,
    GET_APPLICATION_TEMPLATES_FAILED,
    GET_APPLICATIONS_FAILED,
    RELEASE_APPLICATION_TEMPLATE_FAILED,
    UPDATE_APPLICATION_FAILED,
    UPDATE_APPLICATION_TEMPLATE_FAILED
} from "../../store/workshop/constants"
import { workshopActions } from "../../store/workshop/slice"
import store from "../../store/store"
import {
    ApplicationEditType,
    ApplicationTemplate,
    ApplicationTemplateCategory,
    ApplicationTemplateVersion
} from "../../models/workshop/template"
import { TemplateId, TemplateVersionStatus, VersionId } from "../../models/workshop/common"
import {
    ChangeStatusApplicationTemplateRequest,
    CreateApplicationTemplateRequest,
    CreateApplicationTemplateVersionRequest,
    DeleteApplicationTemplateVersionRequest,
    ReleaseApplicationRequest,
    SearchApplicationInstanceRequest,
    SearchApplicationTemplatesRequest,
    SearchApplicationTemplateVersionsRequest,
    UpdateApplicationInstanceRequest,
    UpdateApplicationTemplateRequest
} from "../../models/workshop/api"
import { goToViewPage } from "../../store/workshop/actions"
import { ApplicationInstance } from "../../models/workshop/instance"
import { applicationTemplatesTag } from "../tags"

const baseUrl = "/api/application-workshop/application-template"
const baseUrlInstance = "/api/application-workshop/application-instance"

export const applicationTemplatesApi = queryApi.injectEndpoints({
    endpoints(build) {
        return {
            getApplicationTemplateVersion: build.query<
                ApplicationTemplateVersion,
                { templateId: TemplateId; versionId: VersionId }
            >({
                query: ({ templateId, versionId }) => ({
                    baseUrl,
                    url: "get/version",
                    params: { id: templateId, version_id: versionId }
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const { data: version } = await queryFulfilled

                        if (version) {
                            dispatch(workshopActions.setCurrentApplicationTemplateVersion(version))
                            dispatch(workshopActions.setCurrentApplicationTemplateVersionId(version.Id))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, GET_APPLICATION_TEMPLATE_VERSION_FAILED, e)
                    }
                }
            }),
            getApplicationTemplate: build.query<ApplicationTemplate, TemplateId>({
                query: templateId => ({
                    baseUrl,
                    url: "get/template",
                    params: { id: templateId }
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const { data: template } = await queryFulfilled

                        if (template) {
                            dispatch(workshopActions.setCurrentApplicationTemplate(template))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, GET_APPLICATION_TEMPLATE_FAILED, e)
                    }
                }
            }),
            getApplicationTemplateAllVersions: build.query<ApplicationTemplate[], TemplateId>({
                query: templateId => ({
                    baseUrl,
                    url: "get/all-versions",
                    params: { id: templateId }
                }),
                onQueryStarted: onQueryStartedErrorHandler(GET_APPLICATION_TEMPLATE_ALL_VERSIONS_FAILED)
            }),
            createApplicationTemplate: build.mutation<ApplicationTemplate, CreateApplicationTemplateRequest>({
                query: body => ({
                    baseUrl,
                    url: "create/template",
                    method: "POST",
                    body
                }),
                invalidatesTags: [{ type: applicationTemplatesTag, id: "LIST" }],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const { data: applicationTemplate } = await queryFulfilled

                        if (applicationTemplate) {
                            dispatch(
                                workshopActions.addApplicationTemplateCategory({
                                    Id: applicationTemplate.Id,
                                    Title: applicationTemplate.Title,
                                    Audit: applicationTemplate.Audit,
                                    Versions: []
                                })
                            )
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, CREATE_APPLICATION_TEMPLATE_FAILED, e)
                    }
                }
            }),
            createApplicationTemplateVersion: build.mutation<
                ApplicationTemplateVersion,
                CreateApplicationTemplateVersionRequest
            >({
                query: body => ({
                    baseUrl,
                    url: "create/version",
                    method: "POST",
                    body
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const { data: applicationTemplateVersion } = await queryFulfilled

                        const projectId = store.getState().project.selectProject.data?.id ?? ""

                        if (applicationTemplateVersion) {
                            dispatch(
                                goToViewPage(
                                    projectId,
                                    ApplicationEditType.VERSION_APPLICATION,
                                    applicationTemplateVersion.Id
                                )
                            )
                            dispatch(workshopActions.addApplicationTemplateVersion(applicationTemplateVersion))
                            dispatch(
                                workshopActions.setCurrentApplicationTemplateVersionId(applicationTemplateVersion.Id)
                            )
                            dispatch(workshopActions.setCurrentApplicationTemplateVersion(applicationTemplateVersion))
                            dispatch(workshopActions.setApplicationTemplateCreating(false))
                            dispatch(workshopActions.setEditMode(false))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, CREATE_APPLICATION_TEMPLATE_VERSION_FAILED, e)
                    }
                }
            }),
            updateApplicationTemplate: build.mutation<ApplicationTemplate, UpdateApplicationTemplateRequest>({
                query: body => ({
                    baseUrl,
                    url: "update",
                    method: "PUT",
                    body
                }),
                invalidatesTags: [{ type: applicationTemplatesTag, id: "LIST" }],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const { data: applicationTemplate } = await queryFulfilled

                        if (applicationTemplate) {
                            dispatch(workshopActions.updateApplicationTemplate(applicationTemplate))
                            dispatch(workshopActions.setEditMode(false))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, UPDATE_APPLICATION_TEMPLATE_FAILED, e)
                    }
                }
            }),
            changeStatusApplicationTemplate: build.mutation<
                TemplateVersionStatus,
                ChangeStatusApplicationTemplateRequest
            >({
                query: body => ({
                    baseUrl,
                    url: "status/change",
                    method: "PUT",
                    body
                }),
                async onQueryStarted(requestBody, { dispatch, queryFulfilled }) {
                    try {
                        const { data: status } = await queryFulfilled

                        if (status) {
                            dispatch(
                                workshopActions.changeApplicationTemplateStatus({
                                    Id: requestBody.Id,
                                    VersionId: requestBody.VersionId,
                                    Status: status
                                })
                            )
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, CHANGE_APPLICATION_TEMPLATE_STATUS_FAILED, e)
                    }
                }
            }),
            searchApplicationTemplates: build.query<ApplicationTemplate[], SearchApplicationTemplatesRequest>({
                query: body => ({
                    baseUrl,
                    url: "search/templates",
                    method: "POST",
                    body
                }),
                providesTags: result => [{ type: applicationTemplatesTag, id: "LIST" }],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const { data: applicationTemplates } = await queryFulfilled

                        if (applicationTemplates) {
                            dispatch(workshopActions.setApplicationTemplates(applicationTemplates))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, GET_APPLICATION_TEMPLATES_FAILED, e)
                    }
                }
            }),
            searchApplicationTemplatesCatalog: build.mutation<
                ApplicationTemplateCategory[],
                SearchApplicationTemplatesRequest
            >({
                query: body => ({
                    baseUrl,
                    url: "search/catalogs",
                    method: "POST",
                    body
                }),
                async onQueryStarted({ Title }, { dispatch, queryFulfilled }) {
                    try {
                        const { data: applicationTemplatesCatalog } = await queryFulfilled

                        const tempCategory = store.getState().workshop.currentTempCategory

                        if (applicationTemplatesCatalog) {
                            if (!Title) {
                                dispatch(
                                    workshopActions.setApplicationTemplatesExist(applicationTemplatesCatalog.length > 0)
                                )
                            }

                            const sortedApplicationTemplates = Title
                                ? [...applicationTemplatesCatalog]
                                : [...applicationTemplatesCatalog].sort(
                                      (a, b) => a.Audit.CreateTimestamp - b.Audit.CreateTimestamp
                                  )

                            if (tempCategory && !Title) {
                                sortedApplicationTemplates.push(tempCategory)
                            }

                            dispatch(workshopActions.setApplicationTemplateCatalog(sortedApplicationTemplates))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, GET_APPLICATION_TEMPLATES_FAILED, e)
                    }
                }
            }),
            searchApplicationTemplateVersion: build.query<
                ApplicationTemplateVersion[],
                SearchApplicationTemplateVersionsRequest
            >({
                query: ({ IsReleasingApplication, ...body }) => ({
                    baseUrl,
                    url: "search/versions",
                    method: "POST",
                    body
                }),
                async onQueryStarted({ IsReleasingApplication }, { dispatch, queryFulfilled }) {
                    try {
                        const { data: applicationTemplateVersions } = await queryFulfilled

                        if (applicationTemplateVersions) {
                            if (IsReleasingApplication) {
                                dispatch(
                                    workshopActions.setCurrentApplicationTemplateVersion(applicationTemplateVersions[0])
                                )
                            } else {
                                dispatch(workshopActions.setActualVersions(applicationTemplateVersions))
                            }
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, GET_APPLICATION_TEMPLATE_VERSIONS_FAILED, e)
                    }
                }
            }),
            releaseApplication: build.mutation<ApplicationInstance, ReleaseApplicationRequest>({
                query: body => ({
                    baseUrl: baseUrlInstance,
                    url: "release",
                    method: "POST",
                    body
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const { data: releasedApplication } = await queryFulfilled

                        if (releasedApplication) {
                            dispatch(workshopActions.setCurrentApplicationTemplateVersion(undefined))
                            dispatch(workshopActions.addReleasedApplication(releasedApplication))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, RELEASE_APPLICATION_TEMPLATE_FAILED, e)
                    }
                }
            }),
            deleteApplication: build.mutation<boolean, string>({
                query: templateId => ({
                    baseUrl,
                    url: "delete/template",
                    method: "DELETE",
                    params: { id: templateId }
                }),
                invalidatesTags: [{ type: applicationTemplatesTag, id: "LIST" }],
                async onQueryStarted(templateId, { dispatch, queryFulfilled }) {
                    try {
                        const { data: isTemplateDeleted } = await queryFulfilled

                        if (isTemplateDeleted) {
                            dispatch(workshopActions.deleteApplicationTemplate(templateId))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, DELETE_APPLICATION_TEMPLATE_FAILED, e)
                    }
                }
            }),
            deleteApplicationVersion: build.mutation<boolean, DeleteApplicationTemplateVersionRequest>({
                query: ({ templateId, versionId }) => ({
                    baseUrl,
                    url: "delete/version",
                    method: "DELETE",
                    params: {
                        id: templateId,
                        version_id: versionId
                    }
                }),
                async onQueryStarted({ templateId, versionId }, { dispatch, queryFulfilled }) {
                    try {
                        const { data: isVersionDeleted } = await queryFulfilled

                        if (isVersionDeleted) {
                            dispatch(
                                workshopActions.deleteApplicationTemplateVersion({
                                    templateId,
                                    versionId
                                })
                            )
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, DELETE_APPLICATION_TEMPLATE_VERSION_FAILED, e)
                    }
                }
            }),
            deleteApplicationInstance: build.mutation<ApplicationInstance, { id: string; projectId: string }>({
                query: ({ id, projectId }) => ({
                    baseUrl: baseUrlInstance,
                    url: "delete",
                    method: "DELETE",
                    params: {
                        id,
                        projectId
                    }
                }),
                async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                    try {
                        const { data: deletedApplication } = await queryFulfilled

                        if (deletedApplication) {
                            dispatch(workshopActions.removeReleasedApplication(id))
                            dispatch(workshopActions.removeActualVersion(id))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, DELETE_APPLICATION_TEMPLATE_VERSION_FAILED, e)
                    }
                }
            }),
            searchApplicationInstance: build.query<ApplicationInstance[], SearchApplicationInstanceRequest>({
                query: body => ({
                    baseUrl: baseUrlInstance,
                    url: "search",
                    method: "POST",
                    body
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const { data: applicationInstance } = await queryFulfilled

                        if (applicationInstance) {
                            dispatch(workshopActions.setReleasedApplications(applicationInstance))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, GET_APPLICATIONS_FAILED, e)
                    }
                }
            }),
            updateApplicationInstance: build.mutation<ApplicationInstance, UpdateApplicationInstanceRequest>({
                query: body => ({
                    baseUrl: baseUrlInstance,
                    url: "update",
                    method: "PUT",
                    body
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const { data: applicationInstance } = await queryFulfilled

                        if (applicationInstance) {
                            dispatch(workshopActions.updateApplicationInstance(applicationInstance))
                            dispatch(workshopActions.setCurrentApplicationTemplateVersion(undefined))
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, UPDATE_APPLICATION_FAILED, e)
                    }
                }
            })
        }
    }
})

export const {
    useLazyGetApplicationTemplateVersionQuery,
    useLazyGetApplicationTemplateQuery,
    useLazyGetApplicationTemplateAllVersionsQuery,
    useCreateApplicationTemplateMutation,
    useCreateApplicationTemplateVersionMutation,
    useUpdateApplicationTemplateMutation,
    useChangeStatusApplicationTemplateMutation,
    useLazySearchApplicationTemplatesQuery,
    useSearchApplicationTemplatesCatalogMutation,
    useLazySearchApplicationTemplateVersionQuery,
    useLazySearchApplicationInstanceQuery,
    useReleaseApplicationMutation,
    useDeleteApplicationMutation,
    useDeleteApplicationVersionMutation,
    useDeleteApplicationInstanceMutation,
    useUpdateApplicationInstanceMutation
} = applicationTemplatesApi
